import { useTranslation } from 'react-i18next'
import { RotateCcw } from 'lucide-react'
import type { Project, TaskCard, User } from '@witty-board/shared'
import {
  lastDeletedByActivity,
  useRestoreTaskMutation,
  useTaskActivityQuery,
} from '../../api/tasks.js'
import { formatDateTime } from '../../lib/datetime.js'
import { ProjectSwatch } from '../../components/ProjectSwatch.js'

interface DeletedTaskRowProps {
  task: TaskCard
  project: Project | undefined
  usersById: Map<string, User>
  showProject: boolean
}

/**
 * "Quién" solo existe para tareas, resuelto pidiendo su histórico
 * (`activity` sí lleva `actorId`; `project`/`label` no tienen ningún rastro
 * de auditoría — ver docs/DECISIONS.md, fase 5). Una petición por fila es
 * aceptable con el volumen esperado ("3 usuarios durante mucho tiempo").
 */
export function DeletedTaskRow({ task, project, usersById, showProject }: DeletedTaskRowProps) {
  const { t, i18n } = useTranslation()
  const activity = useTaskActivityQuery(task.id)
  const restore = useRestoreTaskMutation()
  const deletedBy = lastDeletedByActivity(activity.data)
  const actor = deletedBy ? usersById.get(deletedBy.actorId) : undefined

  return (
    <li className="flex h-10 max-md:h-11 items-center gap-3 rounded-card px-2 hover:bg-surface-hi">
      {showProject && project && <ProjectSwatch color={project.color} />}
      <span className="min-w-0 flex-1 truncate text-body text-text">{task.title}</span>
      <span className="shrink-0 text-meta text-text-muted">
        {task.deletedAt &&
          (actor
            ? t('trash.deletedByAt', {
                name: actor.name,
                date: formatDateTime(task.deletedAt, i18n.language),
              })
            : t('trash.deletedAt', { date: formatDateTime(task.deletedAt, i18n.language) }))}
      </span>
      <button
        type="button"
        onClick={() => restore.mutate(task.id)}
        disabled={restore.isPending}
        className="flex h-8 max-md:h-11 shrink-0 items-center gap-1 rounded-card px-2 text-meta text-text-muted hover:bg-surface hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
      >
        <RotateCcw className="h-3 w-3" aria-hidden="true" />
        {t('trash.restore')}
      </button>
    </li>
  )
}
